import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Sparkles, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { canUsePhonics, canUseCustomDetails, getFeatureLimitMessage } from '@/utils/subscriptionUtils';
import GuestModeBanner from '@/components/GuestModeBanner';
import { getLevel } from '@/utils/readingLevels';

const STORY_LENGTHS = ['short', 'medium', 'long'];
const STORY_STYLES = ['adventurous', 'cultural', 'facts and figures', 'fantasy', 'funny', 'gentle', 'historical', 'inspirational', 'magical', 'mystery', 'philosophical', 'realistic', 'sci-fi', 'suspense'];

const TOPIC_CATEGORIES = { 
  'Character Building': ['Accountability','Courage','Determination','Fairness','Honesty','Integrity','Leadership','Perseverance','Respect','Responsibility','Self-Discipline','Setting Boundaries','Sportsmanship','Teamwork'],
  'Helping & Care': ['Acts of Kindness','Animal Care','Care for Younger Children','Community Service','Environmental Awareness','Helping Family','Helping Others','Sharing','Volunteering'],
  'Learning & Growth': ['Adaptability','Critical Thinking','Creativity','Curiosity','Decision Making','Goal Setting','Learn from Mistakes','Observation Skills','Patience in Learning','Problem Solving','Trying New Foods','Time Management'],
  'Life Skills': ['Cultural Celebrations','Decision Making','Financial Literacy','Organization Skills','Optimism','Overcoming Fears','Planning Ahead','Self-Care','Time Management','Understanding Differences','Welcoming New People'],
  'Safety & Health': ['Conflict Resolution','Emergency Awareness','Fire Safety','First Aid Basics','Healthy Habits','Nutrition Awareness','Personal Hygiene','Road Safety','Stranger Danger'],
  'Social & Emotional': ['Active Listening','Empathy','Friendship','Gratitude','Inclusion','Kindness','Managing Emotions','Patience','Respecting Feelings','Self-Awareness','Self-Confidence','Sharing','Teamwork']
};

const INTEREST_CATEGORIES = {
  'Creative Arts': ['baking', 'cooking', 'dance', 'drawing', 'music', 'painting', 'photography', 'sculpting', 'singing', 'theater', 'writing'],
  'Fantasy & Adventure': ['dragons', 'fairies', 'magic', 'mermaids', 'pirates', 'superheroes', 'unicorns', 'wizards'],
  'Nature & Animals': ['birds', 'dinosaurs', 'farm', 'flowers', 'insects', 'ocean', 'pets', 'wildlife', 'reptiles', 'jungle'],
  'Science & Tech': ['coding', 'experiments', 'planets', 'robots', 'space', 'technology', 'volcanoes', 'inventions', 'engineering'],
  'Sports & Activities': ['basketball', 'camping', 'hiking', 'soccer', 'sports', 'swimming', 'tennis', 'cycling', 'skateboarding'],
  'Vehicles & Transport': ['airplanes', 'boats', 'cars', 'excavators', 'helicopters', 'motorcycles', 'trains', 'trucks', 'spaceships']
};

const StoryGenerator = () => {
  const navigate = useNavigate();
  const { user, token, nightMode, refreshUser } = useContext(AuthContext);
  const [children, setChildren] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [customTopic, setCustomTopic] = useState('');
  const [selectedTopicCategory, setSelectedTopicCategory] = useState('');
  const [selectedInterestCategory, setSelectedInterestCategory] = useState('');
  const [otherInterest, setOtherInterest] = useState('');
  const [remainingCredits, setRemainingCredits] = useState(null);
  
  const userTier = user?.subscription_tier || 'free';
  const canPhonics = canUsePhonics(userTier);
  const canCustom = canUseCustomDetails(userTier);
  const isAdmin = user?.role === 'admin';
  const isGuestMode = user?.id === 'guest-user';
  const [formData, setFormData] = useState({
    child_id: '',
    length: '',
    topic: '',
    reading_level: '',
    story_styles: [],
    include_child: true,
    character_ids: [],
    phonics_rhyming: false,
    custom_details: '',
    generate_story_image: true,
    interests: []
  });

  useEffect(() => {
    fetchChildren();
    fetchAvatars();
    fetchUsage();
  }, []);

  useEffect(() => {
    if (children.length > 0 && !formData.child_id) {
      const firstChild = children[0];
      setFormData(prev => ({
        ...prev,
        child_id: firstChild.id,
        reading_level: getLevel(firstChild.reading_level) || 'simple',
        story_styles: firstChild.story_styles?.length > 0 ? firstChild.story_styles : ['realistic'],
        interests: firstChild.interests || []
      }));
    }
  }, [children, formData.child_id]);

  const fetchUsage = async () => {
    if (!token || token === 'guest-token') return;
    try {
      const response = await axios.get(`${API}/auth/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRemainingCredits(response.data.remaining);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const fetchChildren = async () => {
    try {
      // Use optimized endpoint that includes fields needed for generation
      const response = await axios.get(`${API}/children/list/generator`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data || []);
    } catch (error) {
      console.error('Failed to fetch children:', error);
      setChildren([]); // Ensure it's always an array
    }
  };

  const fetchAvatars = async () => {
    try {
      // Use optimized endpoint that only returns id and name
      const response = await axios.get(`${API}/avatars/list/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvatars(response.data || []);
    } catch (error) {
      console.error('Failed to fetch avatars:', error);
      setAvatars([]); // Ensure it's always an array
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (remainingCredits === 0 && !isAdmin) {
      toast.error("You have no credits remaining. Please upgrade your plan.");
      return;
    }

    if (!formData.child_id) {
      toast.error('Please select a child');
      return;
    }

    setGenerating(true);
    setProgress(10);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 2000);

    try {
      const response = await axios.post(
        `${API}/stories/generate`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      clearInterval(progressInterval);
      setProgress(100);
      
      toast.success('Story generated successfully!');
      setTimeout(() => {
        navigate(`/story/${response.data.id}`);
      }, 500);
    } catch (error) {
      clearInterval(progressInterval);
      const errorDetail = error.response?.data?.detail || 'Failed to generate story';
      
      // Handle rate limit error specifically
      if (error.response?.status === 429) {
        toast.error(errorDetail, {
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/subscription')
          },
          duration: 5000
        });
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setGenerating(false);
      setProgress(0);
    }
  };

  const toggleSelection = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const toggleCharacter = (charId) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.character_ids.includes(charId);
      const currentCount = prev.character_ids.length + (prev.include_child ? 1 : 0);

      if (!isCurrentlySelected && currentCount >= 3) {
        toast.error('Maximum of 3 characters allowed per story');
        return prev;
      }

      const newCharacterIds = isCurrentlySelected
        ? prev.character_ids.filter(id => id !== charId)
        : [...prev.character_ids, charId];
      
      return {
        ...prev,
        character_ids: newCharacterIds,
        include_child: newCharacterIds.length === 0 ? true : prev.include_child
      };
    });
  };

  const handleOtherInterestAdd = () => {
    const trimmed = otherInterest.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Please enter an interest');
      return;
    }
    
    if (formData.interests.includes(trimmed)) {
      toast.error('This interest is already added');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      interests: [...prev.interests, trimmed]
    }));
    setOtherInterest('');
  };

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {isGuestMode && <GuestModeBanner />}
      <div className="container mx-auto px-6 py-8 max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="rounded-full w-20"
              disabled={generating}
              style={{
                background: nightMode ? 'var(--bg-secondary)' : 'white',
                color: nightMode ? 'var(--text-primary)' : 'inherit',
                borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
              }}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              Generate Story
            </h1>
          </div>
        </div>

        <div className="story-card" style={{ transform: 'none', transition: 'none' }}>
          <form onSubmit={handleGenerate} className="space-y-5">
            

            {/* Select Child */}
            <div>
              <Label className="text-lg font-semibold">Select Child and Other Characters</Label>
              <div className="flex flex-wrap mt-2">
                {children && children.length > 0 ? children.map((child) => (
                  <button
                    key={child.id}
                    type="button"
                    data-testid={`select-child-${child.id}`}
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        child_id: child.id,
                        reading_level: getLevel(child.reading_level) || 'simple',
                        story_styles: child.story_styles?.length > 0 ? child.story_styles : ['realistic'],
                        interests: child.interests || []
                      }));
                    }}
                    className={`tag-button ${formData.child_id === child.id ? 'selected' : ''}`}
                    style={{
                      background: formData.child_id === child.id ? 'var(--accent-2)' : 'var(--bg-secondary)',
                      color: formData.child_id === child.id ? 'white' : 'var(--text-primary)'
                    }}
                  >
                    {child.name}
                  </button>
                )) : (
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    No children found. Please create a child profile first.
                  </p>
                )}

                {/* Include Child */}
                {children && children.length > 0 && (
                  <div className="flex items-center mx-2 space-x-1">
                    <Checkbox
                      data-testid="include-child-checkbox"
                      id="include_child"
                      checked={formData.include_child}
                      onCheckedChange={(checked) => {
                        if (checked && formData.character_ids.length >= 3) {
                          toast.error('Maximum of 3 characters allowed per story');
                          return;
                        }
                        setFormData({ ...formData, include_child: checked });
                      }}
                      disabled={formData.character_ids.length === 0}
                    />
                    <Label 
                      htmlFor="include_child" 
                      className={`text-sm ${formData.character_ids.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      Include {children.find(c => c.id === formData.child_id)?.name || 'child'} in story
                    </Label>
                  </div>
                )}                

                {/* Separator */}
                {children && children.length > 0 && avatars && avatars.length > 0 && (
                  <div className="mx-2 h-6 w-px bg-gray-300 dark:bg-gray-600" />
                )}

                {/* Select Characters */}
                {avatars && avatars.length > 0 && avatars.map((avatar) => (
                  <button
                    key={avatar.id}
                    type="button"
                    data-testid={`character-${avatar.id}`}
                    onClick={() => toggleCharacter(avatar.id)}
                    className={`tag-button ${formData.character_ids.includes(avatar.id) ? 'selected' : ''}`}
                    style={{
                      background: formData.character_ids.includes(avatar.id) ? 'var(--accent-1)' : 'var(--bg-secondary)',
                      color: formData.character_ids.includes(avatar.id) ? 'white' : 'var(--text-primary)'
                    }}
                  >
                    {avatar.name}
                  </button>
                ))}

              </div>

              {/* Interests Selection (One-off) */}
              {formData.child_id && (
                <div className="mt-4 pt-1 border-t border-gray-100 dark:border-gray-800">
                  <Label className="text-base mb-1 block">
                    Use a different interest for this story (Optional)
                  </Label>

                  {/* Selected Interests Display */}
                  <div className="flex flex-wrap mb-3">
                    {formData.interests.map((interest, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-2 py-1 rounded-full flex items-center gap-1"
                        style={{ background: 'var(--accent-1)', color: 'white' }}
                      >
                        {interest}
                        <button
                          type="button"
                          onClick={() => toggleSelection('interests', interest)}
                          className="ml-1 hover:text-red-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {formData.interests.length === 0 && (
                      <span className="text-xs text-gray-400 italic">No interests selected</span>
                    )}
                  </div>

                  {/* Category Selection */}
                  <div className="flex flex-wrap mb-2">
                    {Object.keys(INTEREST_CATEGORIES).map((category) => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => setSelectedInterestCategory(selectedInterestCategory === category ? '' : category)}
                        className={`tag-button text-xs py-1 px-2 ${selectedInterestCategory === category ? 'selected' : ''}`}
                        style={{
                          background: selectedInterestCategory === category ? 'var(--accent-2)' : 'var(--bg-secondary)',
                          color: selectedInterestCategory === category ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {category}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setSelectedInterestCategory(selectedInterestCategory === 'Other' ? '' : 'Other')}
                      className={`tag-button text-xs py-1 px-2 ${selectedInterestCategory === 'Other' ? 'selected' : ''}`}
                      style={{
                        background: selectedInterestCategory === 'Other' ? 'var(--accent-2)' : 'var(--bg-secondary)',
                        color: selectedInterestCategory === 'Other' ? 'white' : 'var(--text-primary)'
                      }}
                    >
                      Other
                    </button>
                  </div>

                  {/* Sub-groups */}
                  {selectedInterestCategory && selectedInterestCategory !== 'Other' && (
                    <div 
                      className="p-2 rounded-lg mb-2"
                      style={{ background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)'  }}
                    >
                      <div className="flex flex-wrap">
                        {INTEREST_CATEGORIES[selectedInterestCategory].map((interest) => (
                          <button
                            key={interest}
                            type="button"
                            onClick={() => toggleSelection('interests', interest)}
                            className={`tag-button text-xs py-1 px-2 ${formData.interests.includes(interest) ? 'selected' : ''}`}
                            style={{
                              background: formData.interests.includes(interest) ? 'var(--accent-1)' : 'var(--bg-secondary)',
                              color: formData.interests.includes(interest) ? 'white' : 'var(--text-primary)',
                              border: '1px solid var(--sketch-border)'
                            }}
                          >
                            {interest}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Other Input */}
                  {selectedInterestCategory === 'Other' && (
                    <div className="flex gap-2 mb-2">
                      <Input
                        value={otherInterest}
                        onChange={(e) => setOtherInterest(e.target.value)}
                        placeholder="e.g., pottery, origami..."
                        className="h-8 text-sm"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleOtherInterestAdd())}
                        style={{
                          color: 'var(--text-primary)',
                          background: nightMode ? 'var(--bg-secondary)' : 'white'
                        }}
                      />
                      <Button 
                        type="button" 
                        onClick={handleOtherInterestAdd}
                        size="sm"
                        variant="outline"
                        className="h-8"
                        style={{
                          background: nightMode ? 'var(--bg-secondary)' : 'white',
                          color: nightMode ? 'var(--text-primary)' : 'inherit',
                          borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Topic / Moral - Grouped */}
            <div>
              <Label className="text-lg font-semibold">Select Topic/Moral</Label>
              
              {/* Category Selection */}
              <div className="flex-wrap gap-1 mt-2">
                {Object.keys(TOPIC_CATEGORIES).map((category) => (
                  <button
                    key={category}
                    type="button"
                    data-testid={`topic-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => setSelectedTopicCategory(selectedTopicCategory === category ? '' : category)}
                  className={`tag-button whitespace-nowrap ${selectedTopicCategory === category ? 'selected' : ''}`}
                    style={{
                      background: selectedTopicCategory === category ? 'var(--accent-1)' : 'var(--bg-secondary)',
                      color: selectedTopicCategory === category ? 'white' : 'var(--text-primary)',
                      fontWeight: '600'
                    }}
                  >
                    {category}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="topic-category-custom"
                  onClick={() => setSelectedTopicCategory(selectedTopicCategory === 'Custom' ? '' : 'Custom')}
                  className={`tag-button whitespace-nowrap ${selectedTopicCategory === 'Custom' ? 'selected' : ''}`}
                  style={{
                    background: selectedTopicCategory === 'Custom' ? 'var(--accent-1)' : 'var(--bg-secondary)',
                    color: selectedTopicCategory === 'Custom' ? 'white' : 'var(--text-primary)',
                    fontWeight: '600'
                  }}
                >
                  Custom
                </button>
              </div>

              {/* Sub-groups for categories */}
              {selectedTopicCategory && selectedTopicCategory !== 'Custom' && (
                <div 
                  className="p-3 rounded-lg mb-3" 
                  style={{ 
                    border: '2px solid var(--accent-2)', 
                    background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                  }}
                >
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Select from {selectedTopicCategory}:
                  </p>

                  <div className="flex flex-wrap gap-1">
                    {TOPIC_CATEGORIES[selectedTopicCategory].map((topic) => (
                      <button
                        key={topic}
                        type="button"
                        data-testid={`topic-${topic.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => {
                          setFormData({ ...formData, topic });
                          /* setSelectedTopicCategory(''); */
                        }}
                        className={`tag-button nowrap ${formData.topic === topic ? 'selected' : ''}`}
                        style={{
                          background: formData.topic === topic ? 'var(--accent-3)' : 'var(--bg-secondary)',
                          color: formData.topic === topic ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {topic}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom topic input inside frame */}
              {selectedTopicCategory === 'Custom' && (
                <div 
                  className="p-3 rounded-lg mb-3" 
                  style={{ 
                    border: '2px solid var(--accent-2)', 
                    background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                  }}
                >
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Enter your custom topic:
                  </p>
                  <Input
                    data-testid="custom-topic-input"
                    placeholder="e.g., Learning to swim, being brave at the dentist..."
                    value={customTopic}
                    onChange={(e) => {
                      setCustomTopic(e.target.value);
                      setFormData({ ...formData, topic: e.target.value });
                    }}
                    maxLength={100}
                    style={{
                      color: 'var(--text-primary)',
                      background: nightMode ? 'var(--bg-secondary)' : 'white'
                    }}
                  />
                </div>
              )}

              {/* Selected Topic Display */}
              {formData.topic && (
                <div className="mt-2">
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Selected Topic: <span className="text-sm px-3 py-1 rounded-full" style={{ background: 'var(--accent-3)', color: 'white' }}>
                      {formData.topic}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <div>
              <Label className="text-lg font-semibold">Other Features</Label>
  
              {/* Story Length */}
              <div className="flex-wrap gap-1 mt-1">
                <Label className="text-base ">Story Length </Label>
                {STORY_LENGTHS.map((length) => (
                  <button
                    key={length}
                    type="button"
                    data-testid={`length-${length}`}
                    onClick={() => setFormData({ ...formData, length })}
                    className={`tag-button ${formData.length === length ? 'selected' : ''}`}
                    style={{
                      background: formData.length === length ? 'var(--accent-1)' : 'var(--bg-secondary)',
                      color: formData.length === length ? 'white' : 'var(--text-primary)'
                    }}
                  >
                    {length.charAt(0).toUpperCase() + length.slice(1)}
                  </button>
                ))}
              </div>

              {/* Phonics Rhyming */}
              <div className="flex items-center mt-1 space-x-3">
                <Label 
                  htmlFor="phonics_rhyming" 
                  className={`text-base ${canPhonics ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                >
                  Use phonics patterns and rhyming
                </Label>
                <Checkbox
                  data-testid="phonics-rhyming-checkbox"
                  id="phonics_rhyming"
                  checked={formData.phonics_rhyming}
                  onCheckedChange={(checked) => {
                    if (!canPhonics) {
                      toast.error(getFeatureLimitMessage('phonics', userTier), {
                        action: {
                          label: 'Upgrade',
                          onClick: () => navigate('/subscription')
                        }
                      });
                      return;
                    }
                    setFormData({ ...formData, phonics_rhyming: checked });
                  }}
                  disabled={!canPhonics}
                />     
                {!canPhonics && <Crown className="inline w-4 h-4 ml-2" style={{ color: 'var(--accent-2)' }} />}
                                         
              </div>

              {/* Custom Details Field */}
              <div className="mt-2">
                <Label 
                  className={`text-base ${canCustom ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                >
                  Custom Story Details (Optional)
                </Label>
                {!canCustom && <Crown className="inline w-4 h-4 ml-2" style={{ color: 'var(--accent-2)' }} />}
                <Textarea
                  data-testid="custom-details-textarea"
                  placeholder={canCustom ? "Add any specific details you'd like in the story (e.g., 'Include a red bicycle' or 'Set in grandma's garden')..." : "Upgrade to Family tier to use custom details"}
                  value={formData.custom_details}
                  onChange={(e) => {
                    if (!canCustom) {
                      toast.error(getFeatureLimitMessage('customDetails', userTier), {
                        action: {
                          label: 'Upgrade',
                          onClick: () => navigate('/subscription')
                        }
                      });
                      return;
                    }
                    setFormData({ ...formData, custom_details: e.target.value });
                  }}
                  disabled={!canCustom}
                  className={`text-base ${!canCustom ? 'opacity-50 cursor-not-allowed' : ''}`} //min-h-[100px]
                  maxLength={500}
                />
                {canCustom && (
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                    {formData.custom_details.length}/500 characters
                  </p>
                )}
              </div>
            </div>

 
            {/* Generate Button */}
            <div className="pt-2">
  
              {/* Generate Story Image Checkbox */}
              <div>
                <div className="flex items-center space-x-3">
                  <Checkbox
                    data-testid="generate-story-image-checkbox"
                    id="generate_story_image"
                    checked={formData.generate_story_image}
                    onCheckedChange={(checked) => setFormData({ ...formData, generate_story_image: checked })}
                  />
                  <Label htmlFor="generate_story_image" className="text-base cursor-pointer">
                    Generate story image (consume 1 additional credit) 
                  </Label>
                </div>
              </div>

              {userTier === 'free' && (
                <p className="text-xs mt-2 mb-2" style={{ color: 'var(--text-secondary)' }}>
                  Note: Free tier stories are eligible for community featuring by default. You can restrict this in Premium/Family tiers.
                </p>
              )}

              {generating && (
                <div className="mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Generating...</span>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-3" />
                </div>
              )}
              <div 
                className="w-full"
                title={isGuestMode ? 'Sign in to generate stories' : (remainingCredits === 0 && !isAdmin) ? 'No credits remaining' : ''}
              >
                <Button
                  data-testid="generate-story-btn"
                  type="submit"
                  disabled={generating || !formData.child_id || isGuestMode || (remainingCredits === 0 && !isAdmin)}
                  className={`w-full pill-button text-lg py-6 ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{ background: (remainingCredits === 0 && !isAdmin) ? '#94a3b8' : 'var(--accent-2)', color: 'white' }}
                >
                  {generating ? (
                    <>
                      <Loader2 className="mr-2 w-6 h-6 animate-spin" />
                      Generating Story{formData.generate_story_image ? ' and Image...' : '...'}
                    </>
                  ) : (remainingCredits === 0 && !isAdmin) ? (
                    <>
                      Upgrade to create a personalized story
                      <Crown className="ml-2 w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 w-6 h-6" />
                      Generate Story {formData.generate_story_image ? 'and Image' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StoryGenerator;